'use strict';

export default class MxGridHelperService {
	static $inject = [
		'$q',
		'mx.SolutionBuilderAgent.WidgetManager',
		'mx.SolutionBuilderAgent.SchemaService',
		'mx.SolutionBuilderAgent.DialogOpenerService',
		'mx.internationalization',
		'mx.shell.SidePanelService',
		'mx.SolutionBuilderAgent.EditFormTabService',

		'mx.shell.Config',
		'mx.SolutionBuilderAgent.mxColumnDefinitionService',
		'mx.SolutionBuilderAgent.EntityService',
		'mx.SolutionBuilderAgent.WizardService',

		'mx.sb.Agent.DataQuery.Executor',
		'mx.sb.Agent.DataQuery.DataDefinitionRequester',
		'mx.sb.Agent.DataQuery.RelationsRequester',
		'mx.sb.Agent.DataQuery.ClientDataRequester',
		'mx.sb.Agent.DataQuery.MultiFragmentRequester',
		'mx.sb.Agent.DataQuery.ServiceRequester',
		'mx.sb.Agent.DataQuery.DataQueryDefinition',
		'mx.sb.Agent.DataQuery.Enums',
		'mx.SolutionBuilderAgent.GridEnums',
	];

	constructor(
		$q,
		widgetManager,
		schemaService,
		dialogOpenerService,
		i18n,
		sidePanelService,
		editFormTabService,
		configService,
		columnDefinitionService,
		entityService,
		wizardService,
		DataQueryExecutor,
		DataDefinitionRequester,
		RelationsRequester,
		ClientDataRequester,
		MultiFragmentRequester,
		ServiceRequester,
		DataQueryDefinition,
		DataQueryEnums,
		GridEnums
	) {
		Object.assign(this, {
			$q,
			widgetManager,
			schemaService,
			dialogOpenerService,
			i18n,
			sidePanelService,
			editFormTabService,

			configService,
			columnDefinitionService,
			entityService,
			wizardService,

			DataQueryExecutor,
			DataDefinitionRequester,
			RelationsRequester,
			ClientDataRequester,
			MultiFragmentRequester,
			ServiceRequester,
			DataQueryDefinition,
			DataQueryEnums,
			GridEnums,
		});

		const dataSourceConfig = configService.get('dataSource') || {};
		this.defaultGridPreprocessorsConfig = {
			preprocessors: {
				//config of preprocessors and default values (not personalized) for preprocessor for this grid.
				paging: {
					enabled: true,
					pageSize: dataSourceConfig.pageSize,
					pageSizes: dataSourceConfig.pageSizes,
				},
				sorting: {
					enabled: true,
				},
				search: {
					enabled: true,
				},
				columnSearch: {
					enabled: true,
				},
				advanceSearch: {
					enabled: false,
				},
			},
		};

		this.wizardObjectIdBydId = {};
	}

	async initExecutor(dataQuery, personalizationKey, preprocessorsConfig) {
		const executor = new this.DataQueryExecutor();
		let requester;
		const config = angular.merge({}, this.defaultGridPreprocessorsConfig, preprocessorsConfig);

		//TODO: may be initialization of requester should be done in DataQueryDefinition constructor?
		if (dataQuery.IsStatic) {
			requester = new this.ClientDataRequester(dataQuery);
		} else if (dataQuery.Service) {
			requester = new this.ServiceRequester(dataQuery);
		} else if (dataQuery.IsMultiFragment) {
			requester = new this.MultiFragmentRequester(dataQuery);
		} else if (dataQuery.IsRelation) {
			requester = new this.RelationsRequester(dataQuery);
		} else {
			requester = new this.DataDefinitionRequester(dataQuery);
		} //TODO: else ServiceRequester

		await executor.init(dataQuery, requester, config);

		await executor.initPersonalization(personalizationKey);
		return executor;
	}

	getDefaultDQ(entityClassName, isMultifragment) {
		return (
			isMultifragment
				? this.columnDefinitionService.loadColumnsForClass(entityClassName)
				: this.$q.when([
						{
							DisplayType: this.DataQueryEnums.ColumnDisplayTypeEnum.Visible,
							Expression: 'DisplayString',
							AttributeValueType: mx.shell.ObjectPropertyType.String.name,
							Position: 0,
							Sorting: this.DataQueryEnums.ColumnSortingEnum.Ascending,
							Name: 'DisplayString',
							Title: this.i18n.get('solutionBuilderAgent.services.columnDefinition.displayStringColumnTitle'),
						},
				  ])
		).then((columns) => {
			return new this.DataQueryDefinition({
				Columns: columns,
				SchemaClassName: entityClassName,
			});
		});
	}

	initDQByDefinition(dataQueryName, entityClassName, type = this.GridEnums.DataDefinitionGridTypes.Objects) {
		const isMultiFragment = type === this.GridEnums.DataDefinitionGridTypes.MultiFragment;
		const isRelation = type === this.GridEnums.DataDefinitionGridTypes.Relation;
		return (
			dataQueryName
				? this.columnDefinitionService.getGridDefinitionByName(dataQueryName).catch(() => {
						return this.columnDefinitionService.getGridDefinitionByClassName(entityClassName);
				  })
				: this.columnDefinitionService.getGridDefinitionByClassName(entityClassName)
		)
			.catch((error) => {
				if (!entityClassName) {
					return this.$q.reject(error);
				}
				return this.getDefaultDQ(entityClassName, isMultiFragment);
			})
			.then((dataQuery) => {
				dataQuery.IsMultiFragment = isMultiFragment;
				dataQuery.IsRelation = isRelation;
				return dataQuery;
			});
	}

	initStaticDQ(columns) {
		columns = (columns || []).map((col) => {
			const column = angular.copy(col);
			switch (column.type || column.Type) {
				case 'icon':
					column.AttributeValueType = column.Type = mx.shell.ObjectPropertyType.Icon.name;
					break;
				case 'color':
					column.AttributeValueType = column.Type = mx.shell.ObjectPropertyType.Color.name;
					break;
				case 'string':
					column.AttributeValueType = column.Type = mx.shell.ObjectPropertyType.String.name;
					break;
				case 'integer':
				case 'number':
					column.AttributeValueType = column.Type = mx.shell.ObjectPropertyType.Integer.name;
					break;
				case 'decimal':
					column.AttributeValueType = column.Type = mx.shell.ObjectPropertyType.Decimal.name;
					break;
				case 'float':
					column.AttributeValueType = column.Type = mx.shell.ObjectPropertyType.Float.name;
					break;
				case 'date':
					column.AttributeValueType = column.Type = mx.shell.ObjectPropertyType.Date.name;
					break;
				case 'currency':
					column.AttributeValueType = column.Type = mx.shell.ObjectPropertyType.Currency.name;
					break;
				case 'boolean':
					column.AttributeValueType = column.Type = mx.shell.ObjectPropertyType.Boolean.name;
					break;
				case 'enumeration':
					column.AttributeValueType = column.Type = mx.shell.ObjectPropertyType.Integer.name;
					column.ReferenceTo = column.ReferenceTo || column.className;
					break;
				case 'pickup':
					column.AttributeValueType = column.Type = mx.shell.ObjectPropertyType.Guid.name;
					column.ReferenceTo = column.ReferenceTo || column.className;
					break;
				default:
					column.AttributeValueType = column.Type = mx.shell.ObjectPropertyType.String.name;
			}
			delete column.type;
			delete column.className;

			column.Width = column.Width || column.width;
			delete column.width;

			column.Name = column.Name || column.name;
			delete column.name;

			column.Position = column.Position || column.position;
			delete column.position;

			column.Title = column.Title || column.title;
			delete column.title;

			if (typeof column.visible === 'boolean' && typeof column.DisplayType === 'undefined') {
				column.DisplayType = column.visible ? this.DataQueryEnums.ColumnDisplayTypeEnum.Visible : this.DataQueryEnums.ColumnDisplayTypeEnum.Hidden;
			}

			column.Alignment = column.Alignment || column.align;
			delete column.align;

			column.Expression = column.Expression || column.ValuePropertyName || column.field || column.Name;
			delete column.ValuePropertyName;
			delete column.field;

			return column;
		});

		return new this.DataQueryDefinition({
			IsStatic: true,
			Columns: columns,
		});
	}

	async getEntityTypesForCreate(entityTypes = []) {
		const result = [];
		for (let i = 0; i < entityTypes.length; i++) {
			if (await this.widgetManager.hasNewDialogs(entityTypes[i]._type)) {
				result.push(entityTypes[i]);
			}
		}
		return result;
	}

	getAllowedEntityTypes(entityClassName, allowedTypes) {
		allowedTypes = allowedTypes ? allowedTypes.split(',') : [];
		return this.schemaService.getTypeItems(undefined, entityClassName).then((types) => {
			return allowedTypes.length ? types.filter((type) => allowedTypes.indexOf(type.Name) >= 0) : types;
		});
	}

	runGenericPreviewAction(data) {
		//TODO: switch item._typeType case
		return this.widgetManager.getPreviewsOrDialogs(data.item._type).then((views) => {
			if (views && views.length) {
				return this.dialogOpenerService.openPreview(data.item, undefined, undefined, data.parentView);
			} else {
				return this.$q.reject('Details view does not exist or permission is denied for this Role');
			}
		});
	}

	runGenericCreateAction(data) {
		//TODO: switch entityTypeType case
		return this.widgetManager.getNewDialogs(data._type).then((views) => {
			if (views && views.length) {
				return this.dialogOpenerService.openNewDialog(data._type, undefined, undefined, undefined, data.presetFields, data.parentView, false, undefined, true)
					.newEntityCreatedPromise;
			} else {
				return this.$q.reject('Create New view does not exist or permission is denied for this Role');
			}
		});
	}

	runGenericEditAction(data) {
		//TODO: switch entityTypeType case
		return this.widgetManager.getEditDialogs(data.item._type).then((views) => {
			if (views && views.length) {
				return this.dialogOpenerService.openEditDialog(data.item, true, data.presetFields, data.parentView).finishDialogInteractionPromise;
			} else {
				return this.$q.reject(mx.warnCatchMessage('Edit view does not exist or permission is denied for this Role'));
			}
		});
	}

	openEditForm(options, parentView) {
		if (options.templateId) {
			return this.editFormTabService.open(options, parentView);
		} else {
			return this.$q.reject('templateId is mandatory for EditForm');
		}
	}

	getWizardObjectId(wizardId) {
		this.wizardObjectIdBydId[wizardId] =
			this.wizardObjectIdBydId[wizardId] ||
			this.entityService
				.getFragmentDescriptors({
					entityClass: 'PDRContentWidgetClassWizard',
					ids: [wizardId],
					useObjectId: false,
				})
				.then((result) => ((result && result[0]) || {}).ObjectId);

		return this.wizardObjectIdBydId[wizardId];
	}

	openWizard(options) {
		return this.getWizardObjectId(options.wizardId).then((wizardObjectId) => {
			const wizardView = this.wizardService.getWizardView({
				widgetId: wizardObjectId,
				wizardMode: mx.SolutionBuilderAgent.WizardMode.Default,
				name: options.wizardHeader,
				displayName: options.wizardHeader,
				selectedObjects: [options.entity],
				outerContext: options.outerContext,
				customHeader: options.wizardHeader,
			});

			wizardView.id = Date.now();

			return this.sidePanelService.addView(wizardView, options.parentView, true);
		});
	}
}
